/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-06-25
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Selecteur de langue du site UK.
 *
 * La liste des langues vient de `supported`, expose par le fournisseur, et non
 * d'une constante locale : ajouter une langue se fait alors en un seul endroit,
 * `i18n/LanguageContext.jsx`.
 *
 * `darkMode` existe parce que le selecteur apparait aussi sur fond sombre, ou
 * les bordures ardoise claires de la barre de navigation disparaissent.
 */
import React from "react";
import { useLang, LANG_LABELS } from "../i18n/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Globe, Check } from "lucide-react";
import Flag from "./Flag";

const LanguageSwitcher = ({ darkMode = false }) => {
  const { lang, setLang, supported } = useLang();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="lang-switcher-trigger"
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
          darkMode
            ? "border-slate-700 text-slate-100 hover:bg-slate-800"
            : "border-slate-200 text-slate-700 hover:bg-slate-100"
        }`}
      >
        <Globe className="h-4 w-4" />
        {LANG_LABELS[lang] && <Flag code={LANG_LABELS[lang].flag} />}
        <span className="uppercase">{lang}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]">
        {supported.map((code) => (
          <DropdownMenuItem
            key={code}
            data-testid={`lang-option-${code}`}
            onClick={() => setLang(code)}
            className="cursor-pointer flex items-center justify-between gap-3"
          >
            <span className="flex items-center gap-2">
              <Flag code={LANG_LABELS[code].flag} />
              <span>{LANG_LABELS[code].native}</span>
            </span>
            {lang === code && <Check className="h-4 w-4 text-blue-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
